import React, { useState, useEffect, useRef } from "react";
import { Checkbox } from "antd";

import listChangeHoc from "./listChangeHOC";

import BetterList from "./user-add-better-list";

import {
    IItemData,
    ChangeParams,
    IsCheckAllParams,
    CheckAllParams,
} from "./common.typing";

import { CheckboxChangeEvent } from "antd/lib/checkbox/Checkbox";

import locale from "../../_util/locale";
import SvgIcon from "../../assets/icons";
import { useListKeyboardNavigation } from "./hooks/useListKeyboardNavigation";

export interface IOrgListProps {
    type?: string;
    currentList: IItemData[];
    setCurrentList: (list: IItemData[]) => void;
    orgList?: IItemData[];
    orgUser?: IItemData[];
    disabledids?: string[];
    onClickNextDep: (org: IItemData) => void;
    onChangeHoc: (params: ChangeParams) => void;
    onCheckAllHoc: (params: CheckAllParams) => void;
    isCheckAllHoc: (params: IsCheckAllParams) => boolean;
    isSubDisabled: (org: IItemData) => boolean;
    requiredList?: IItemData[];
    multiple?: boolean;
}

const OrgList: React.FC<IOrgListProps> = ({
    type,
    currentList,
    setCurrentList,
    orgList = [],
    orgUser = [],
    disabledids = [],
    requiredList,
    onClickNextDep,
    onChangeHoc,
    onCheckAllHoc,
    isCheckAllHoc,
    isSubDisabled,
    multiple = true,
}) => {
    // ==================== 分页状态 ====================
    const limit = 50;
    const [page, setPage] = useState(1);
    const [pageOrgUser, setPageOrgUser] = useState(orgUser.slice(0, limit * page));

    // ==================== Refs ====================
    const orgListRef = useRef<HTMLDivElement>(null);
    const pageRef = useRef(page);
    const orgUserRef = useRef(orgUser);
    const currentListRef = useRef(currentList);

    useEffect(() => { pageRef.current = page; }, [page]);
    useEffect(() => { orgUserRef.current = orgUser; }, [orgUser]);
    useEffect(() => { currentListRef.current = currentList; }, [currentList]);

    /** 不可选的部门 */
    const isOrgDisabled = (id: string | number) => {
        const curId = `${id.toString().split("-org-")[0]}-org-`;
        const requiredIds = requiredList
            ? requiredList.map(v => `${v.id.toString().split("-org-")[0]}-org-`)
            : [];
        return disabledids.includes(curId) || requiredIds.includes(curId);
    };

    /** 不可选的成员 */
    const isUserDisabled = (id: string | number) => {
        const requiredIds = requiredList ? requiredList.map(v => v.id) : [];
        return disabledids.includes(id.toString()) || requiredIds.includes(id);
    };

    // ==================== 导航列表（合并组织+用户） ====================
    const navigationList = React.useMemo(() => {
        const result: Array<IItemData & { itemType: 'org' | 'user' }> = [];
        orgList.forEach(org => result.push({ ...org, itemType: 'org' }));
        if (type !== "org") {
            pageOrgUser.forEach(user => result.push({ ...user, itemType: 'user' }));
        }
        return result;
    }, [orgList, pageOrgUser, type]);

    // 导航列表相关 refs
    const navigationListRef = useRef(navigationList);
    const prevNavigationListLengthRef = useRef(navigationList.length);
    const isLoadingMoreRef = useRef(false);
    const targetIndexAfterLoadRef = useRef<number | null>(null);
    const pendingScrollIndexRef = useRef<number | null>(null);

    // 检查是否还有更多数据
    const hasMoreData = orgUser.length > limit && pageOrgUser.length < orgUser.length;
    const hasMoreDataRef = useRef(hasMoreData);
    useEffect(() => { hasMoreDataRef.current = hasMoreData; }, [hasMoreData]);

    const loadMoreForKeyboard = (targetIndex: number) => {
        if (!hasMoreDataRef.current) return;
        isLoadingMoreRef.current = true;
        targetIndexAfterLoadRef.current = targetIndex;
        pendingScrollIndexRef.current = targetIndex;
        const fpage = pageRef.current + 1;
        setPage(fpage);
        setPageOrgUser(orgUserRef.current.slice(0, limit * fpage));
    };

    const { highlightedIndex, setHighlightedIndex, handlePointerMove, scrollToItem } = useListKeyboardNavigation({
        containerRef: orgListRef,
        listRef: navigationListRef,
        dataAttribute: "data-org-index",
        stickyHeaderOffset: 50,
        hasMoreDataRef,
        onLoadMore: loadMoreForKeyboard,
        onSelectItem: (index, selectedItem) => {
            const selectedList = currentListRef.current;
            const isCurrentlySelected = selectedList.some(item => item.id == selectedItem.id);
            if (selectedItem.itemType === "org") {
                const list = type === "org" ? [...orgList] : [...orgList, ...orgUser];
                onChangeHoc({
                    value: selectedItem.id,
                    checked: !isCurrentlySelected,
                    list,
                    currentList: selectedList,
                    setCurrentList,
                });
            } else if (selectedItem.itemType === "user") {
                onChangeHoc({
                    value: selectedItem.id,
                    checked: !isCurrentlySelected,
                    list: [...orgUser],
                    currentList: selectedList,
                    setCurrentList,
                });
            }
        },
        isItemDisabled: (item) =>
            item.itemType === "org" ? isOrgDisabled(item.id) : isUserDisabled(item.id),
    });

    // 处理导航列表变化（加载更多或切换数据源）
    useEffect(() => {
        const prevLength = prevNavigationListLengthRef.current;
        const currentLength = navigationList.length;
        navigationListRef.current = navigationList;
        prevNavigationListLengthRef.current = currentLength;

        if (isLoadingMoreRef.current && currentLength > prevLength) {
            isLoadingMoreRef.current = false;
            if (targetIndexAfterLoadRef.current !== null) {
                const targetIndex = targetIndexAfterLoadRef.current;
                targetIndexAfterLoadRef.current = null;
                setHighlightedIndex(targetIndex);
            }
            return;
        }

        setHighlightedIndex(-1);
    }, [navigationList, setHighlightedIndex]);

    // 加载更多后滚动到目标位置
    useEffect(() => {
        if (pendingScrollIndexRef.current !== null && pendingScrollIndexRef.current < navigationList.length) {
            const targetIndex = pendingScrollIndexRef.current;
            pendingScrollIndexRef.current = null;
            requestAnimationFrame(() => scrollToItem(targetIndex));
        }
    }, [navigationList, scrollToItem]);

    // ==================== 分页逻辑 ====================
    useEffect(() => {
        setPageOrgUser(orgUser.slice(0, limit));
        setPage(1);
    }, [orgUser]);

    const loadMore = () => {
        const fpage = page + 1;
        setPage(fpage);
        setPageOrgUser(orgUser.slice(0, limit * fpage));
    };
    /**
     * 全选组织架构和人
     * @param e CheckboxChangeEvent
     */
    const onCheckAll = (e: CheckboxChangeEvent) => {
        const { value, checked } = e.target;
        const list = type === "org" ? [...orgList] : [...orgList, ...orgUser];
        onCheckAllHoc({
            value,
            checked,
            list,
            currentList,
            setCurrentList,
            requiredList,
            disabledids,
        });
    };

    // const setPageOrgUser: Function = () => {}

    /**
     * 选择组织架构
     * @param e CheckboxChangeEvent
     */
    const onChooseOrg = (e: CheckboxChangeEvent) => {
        const { value, checked } = e.target;
        const list = type === "org" ? [...orgList] : [...orgList, ...orgUser];
        onChangeHoc({
            value,
            checked,
            list,
            currentList,
            setCurrentList,
        });
    };

    /**
     * 选择组织架构下的人
     * @param e CheckboxChangeEvent
     */
    const onChooseOrgUser = (e: CheckboxChangeEvent) => {
        const { value, checked } = e.target;
        onChangeHoc({
            value,
            checked,
            list: [...orgUser],
            currentList,
            setCurrentList,
        });
    };

    return (
        <div className="orgList" id="userListComponent" ref={orgListRef}>
            {multiple && (
                <div className="orgListCheckAll">
                    <Checkbox
                        onChange={onCheckAll}
                        checked={isCheckAllHoc({
                            list:
                                type === "org"
                                    ? [...orgList]
                                    : [...orgList, ...orgUser],
                            currentList,
                            disabledids,
                        })}
                    >
                        <span className="name">
                            {locale("useradd_all_select")}
                        </span>
                    </Checkbox>
                </div>
            )}

            <Checkbox.Group
                style={{ width: "100%" }}
                value={currentList.map(v => v.id)}
            >
                {orgList &&
                    orgList.map((org, orgIndex) => {
                        return (
                            <div
                                className={`orgListCheck ${highlightedIndex === orgIndex ? 'highlighted': ''}`}
                                key={org.id}
                                data-org-index={orgIndex}
                                onPointerMove={() => handlePointerMove(orgIndex)}
                            >
                            <Checkbox
                                value={org.id}
                                onChange={onChooseOrg}
                                disabled={isOrgDisabled(org.id)}
                            >
                                <div className="orgName">
                                    {org.name}
                                    {!!org.num && (
                                        <span className="userNum">{`（${org.num}）`}</span>
                                    )}
                                </div>
                            </Checkbox>
                            <div
                                className={`orgSubBox ${
                                    isSubDisabled(org) ? "disabled" : ""
                                }`}
                                onClick={() => onClickNextDep(org)}
                            >
                                {/* <IconFont type="icon-tree-next" /> */}
                                <SvgIcon component="TreeNext" />
                                <span className="orgSub">
                                    {locale("useradd_all_child")}
                                </span>
                            </div>
                        </div>
                        );
                    })}
                {type !== "org" &&
                    pageOrgUser &&
                    pageOrgUser.map((item, userIndex) => {
                        const navigationIndex = orgList.length + userIndex; // 用户项目在导航列表中的索引
                        return (
                            <div
                                key={item.id}
                                data-org-index={navigationIndex}
                                className={
                                    highlightedIndex === navigationIndex ? 'highlighted' : ''}
                                onPointerMove={() => handlePointerMove(navigationIndex)}
                            >
                                <BetterList
                                    isBetter={page > 1}
                                    user={item}
                                    onChooseOrgUser={onChooseOrgUser}
                                    disabled={isUserDisabled(item.id)}
                                />
                            </div>
                        );
                    })}
            </Checkbox.Group>
            {orgUser.length > limit && pageOrgUser.length < orgUser.length && (
                <div className="more" onClick={loadMore}>
                    {locale("load_more_text")}
                </div>
            )}
        </div>
    );
};

export default listChangeHoc(OrgList);
